import Concertina from "@components/Concertina/Concertina";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { CMSFootnotes } from "@types";
import React from "react";
import { AnswerQuestion, FaqAccordionContent, FaqAccordionFields, FaqTextContent } from "types/CMSFaqProps";

import style from "./style.module.scss";

export interface ItemObject {
    title: React.JSX.Element;
    titleString?: string;
    content: React.JSX.Element;
}

interface FaqBlockProps {
    content: FaqAccordionContent[] | FaqTextContent[];
    placement?: string;
    locale?: string;
    sectionIndex?: number;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    title?: string;
    footnotes?: CMSFootnotes[];
    jnjFullBranded?: boolean;
}

const buildFaqAccordion = (
    accordion: FaqAccordionFields,
    footnotes: CMSFootnotes[],
    placement?: string,
    jnjFullBranded?: boolean,
): JSX.Element => {
    const type = accordion?.accordion_type;

    const items: ItemObject[] =
        accordion?.panes &&
        accordion?.panes?.map(({ pane }) => {
            const title = <JMCHtml footnotes={footnotes}>{pane?.question}</JMCHtml>;
            const titleString = pane?.question;
            const content = <JMCHtml footnotes={footnotes}>{pane?.answer}</JMCHtml>;
            return {
                title,
                titleString,
                content,
            };
        });

    return (
        <>
            {items?.length && (
                <Concertina
                    items={items}
                    type={type}
                    placement={placement}
                    titleComponent={jnjFullBranded ? "h5" : "h3"}
                />
            )}
        </>
    );
};

const buildFaqText = (text: AnswerQuestion, footnotes: CMSFootnotes[], jnjFullBranded: boolean): JSX.Element => {
    const { question, answer } = text;

    return (
        <>
            <Typography variant={jnjFullBranded ? "h5" : "h3"}>
                <JMCHtml footnotes={footnotes}>{question}</JMCHtml>
            </Typography>
            {jnjFullBranded ? (
                <Typography variant="body-02">
                    <JMCHtml footnotes={footnotes}>{answer}</JMCHtml>
                </Typography>
            ) : (
                <JMCHtml footnotes={footnotes}>{answer}</JMCHtml>
            )}
        </>
    );
};

export const FaqBlock = ({ content, title, footnotes, placement, jnjFullBranded }: FaqBlockProps): JSX.Element => {
    return (
        <>
            {title && (
                <Typography variant="h3">
                    <JMCHtml>{title}</JMCHtml>
                </Typography>
            )}
            {content?.length &&
                content?.map((content: FaqAccordionContent | FaqTextContent, index: number) => {
                    const key = `faqAccordion-${index}`;
                    let component: JSX.Element;
                    if ("accordion" in content) {
                        component = buildFaqAccordion(content.accordion, footnotes, placement, jnjFullBranded);
                    }
                    if ("text" in content) {
                        component = buildFaqText(content.text, footnotes, jnjFullBranded);
                    }
                    return (
                        <div key={key} className={jnjFullBranded && "text" in content ? style.faqBlock : null}>
                            {component}
                        </div>
                    );
                })}
        </>
    );
};
